import { Calendar, getCalendarEntry } from 'owa-calendar-cache';
import loc, { format } from 'owa-localize';

import type CalendarColor from 'owa-service/lib/contract/CalendarColor';
import type { CalendarEntry } from 'owa-graph-schema';
import { ControlIcons } from 'owa-control-icons';
import { action } from 'satcheljs/lib/legacy';
import { calendarNotificationsBarSetCalendarColor } from 'owa-locstrings/lib/strings/calendarnotificationsbarsetcalendarcolor.locstring.json';
import { calendarNotificationsBarSetCalendarColorFailed } from 'owa-locstrings/lib/strings/calendarnotificationsbarsetcalendarcolorfailed.locstring.json';
import handleCalendarServiceError from './handleCalendarServiceError';
import { lazyShowCalendarNotification } from 'owa-calendar-notification-bar';
import setCalendarColorService from '../services/setCalendarColorService';
import showCalendarErrorNotification from './showCalendarErrorNotification';
import { trace } from 'owa-trace';
import { tryAgainLabel } from 'owa-locstrings/lib/strings/tryagainlabel.locstring.json';
import { updateCalendarColorScheme } from '../utils/setCalendarColorUtils';

/**
 * Action to set the calendar color
 */
export let setCalendarColor = action('setCalendarColor')(async function setCalendarColorInternal(
    calendar: Calendar,
    newColor: CalendarColor
): Promise<void> {
    let entry = getCalendarEntry(calendar);

    return handleCalendarServiceError(
        constructRequestCallback(calendar, entry, newColor),
        constructRequestFailedCallback(calendar, entry, entry.CalendarColor, newColor)
    ).catch(error => {
        return Promise.resolve();
    });
});

function constructRequestCallback(
    calendar: Calendar,
    entry: CalendarEntry,
    newColor: CalendarColor
) {
    return async () => {
        const response = await setCalendarColorService(calendar.id, newColor);
        trace.info(
            `[setCalendarColor] Successfully set new calendar color, calendar: ${calendar.displayName}, color: ${newColor}`
        );

        updateCalendarColorScheme(calendar, entry, newColor);

        lazyShowCalendarNotification.import().then(showCalendarNotification => {
            showCalendarNotification(
                'setCalendarColor',
                'Surface',
                format(loc(calendarNotificationsBarSetCalendarColor), calendar.displayName),
                { icon: ControlIcons.Save }
            );
        });

        return response;
    };
}

function constructRequestFailedCallback(
    calendar: Calendar,
    calendarEntry: CalendarEntry,
    oldColor: CalendarColor,
    newColor: CalendarColor
) {
    return () => {
        updateCalendarColorScheme(calendar, calendarEntry, oldColor);

        showCalendarErrorNotification(
            'setCalendarColor',
            format(loc(calendarNotificationsBarSetCalendarColorFailed), calendar.displayName),
            ControlIcons.Error,
            loc(tryAgainLabel),
            () => setCalendarColor(calendar, newColor)
        );
    };
}

export default setCalendarColor;
